import { useEffect, useMemo, useState } from "react";
import { NavLink as NavLinkRRD, Link, useLocation, useNavigate } from "react-router-dom";
import { PropTypes } from "prop-types";
import { useDispatch } from "react-redux";
import { LoginDetails } from "../../reduxToolkit/slice/auth";
import Swal from "sweetalert2";

/* ───────────────────────── Icons (simple line icons) ───────────────────────── */
const svgProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
const ICONS = {
  "Dashboard": <svg {...svgProps}><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>,
  "Companies": <svg {...svgProps}><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 21v-5h6v5" /><path d="M9 10h.01M15 10h.01" /></svg>,
  "Catalog Setup": <svg {...svgProps}><path d="M4 6h16M4 12h16M4 18h10" /></svg>,
  "Plans": <svg {...svgProps}><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" /></svg>,
  "Audit Log": <svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  "Invoices": <svg {...svgProps}><path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3Z" /><path d="M9 8h6M9 12h6M9 16h3" /></svg>,
  "Email Templates": <svg {...svgProps}><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>,
  "SMS Templates": <svg {...svgProps}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1.1-4.4A8 8 0 1 1 21 12Z" /><path d="M8.5 12h.01M12 12h.01M15.5 12h.01" /></svg>,
  "default": <svg {...svgProps}><circle cx="12" cy="12" r="3" /></svg>,
};
const iconFor = (name) => ICONS[name] || ICONS.default;

const Chevron = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: "transform .2s", transform: open ? "rotate(180deg)" : "none", flexShrink: 0, opacity: 0.7 }}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const SignOutIcon = () => (
  <svg {...svgProps}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></svg>
);

/* ───────────────────────── Active-page matching ─────────────────────────
   Each menu item matches its own path plus any related pages listed in `activeFor`
   (e.g. Companies also covers /user/view and /change-password). Only the single best
   (longest) match is highlighted, so two sections can never light up at once. */
const fullPath = (item, p) => `${item.layout}${p}`;
const matches = (pathname, prefix) => pathname === prefix || pathname.startsWith(prefix + "/");

const collectLeaves = (items) => {
  const leaves = [];
  items.forEach((item) => {
    if (item.subMenu?.length) item.subMenu.forEach((sub) => leaves.push({ ...sub, parentName: item.name }));
    else leaves.push(item);
  });
  return leaves;
};

const findActiveLeaf = (leaves, pathname) => {
  let best = null;
  let bestLen = -1;
  leaves.forEach((leaf) => {
    const prefixes = [leaf.path, ...(leaf.activeFor || [])].map((p) => fullPath(leaf, p));
    prefixes.forEach((prefix) => {
      if (matches(pathname, prefix) && prefix.length > bestLen) {
        best = leaf;
        bestLen = prefix.length;
      }
    });
  });
  return best;
};

/* ───────────────────────── Styles ───────────────────────── */
const COLORS = {
  bg: "#0f172a",
  border: "rgba(255,255,255,.07)",
  text: "rgba(226,232,240,.72)",
  textMuted: "rgba(226,232,240,.45)",
  hover: "rgba(255,255,255,.06)",
  activeBg: "rgba(173,36,41,.18)",
  red: "#e0474d",
  brand: "#AD2429",
};

const css = `
.bbz-sb { position: fixed; left: 0; top: 0; bottom: 0; width: 240px; background: ${COLORS.bg}; display: flex; flex-direction: column; z-index: 100; text-align: left; font-family: 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif; }
.bbz-sb * { box-sizing: border-box; }
.bbz-sb-brand { padding: 20px 18px 16px; border-bottom: 1px solid ${COLORS.border}; flex-shrink: 0; }
.bbz-sb-logo { display: inline-flex; align-items: center; padding: 6px 10px; background: #fff; border-radius: 8px; }
.bbz-sb-role { font-size: 10px; color: ${COLORS.textMuted}; margin-top: 8px; letter-spacing: .12em; text-transform: uppercase; }
.bbz-sb-nav { flex: 1; overflow-y: auto; padding: 14px 10px; }
.bbz-sb-nav::-webkit-scrollbar { width: 4px; }
.bbz-sb-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,.12); border-radius: 4px; }
.bbz-sb-item { position: relative; display: flex; align-items: center; gap: 12px; width: 100%; padding: 9px 12px; margin-bottom: 2px; border: 0; border-radius: 8px; background: transparent; color: ${COLORS.text}; font-family: 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px; font-weight: 500; text-align: left; text-decoration: none; cursor: pointer; transition: background .15s, color .15s; }
.bbz-sb-item:hover { background: ${COLORS.hover}; color: #fff; text-decoration: none; }
.bbz-sb-item:focus { outline: none; }
.bbz-sb-item:focus-visible { outline: none; box-shadow: inset 0 0 0 1px rgba(255,255,255,.25); }
.bbz-sb-subitem:focus { outline: none; }
.bbz-sb-item .bbz-sb-icon { display: inline-flex; width: 18px; justify-content: center; flex-shrink: 0; }
.bbz-sb-item .bbz-sb-label { flex: 1; }
.bbz-sb-item.is-active { background: ${COLORS.activeBg}; color: #fff; font-weight: 600; }
.bbz-sb-item.is-active::before { content: ''; position: absolute; left: -10px; top: 8px; bottom: 8px; width: 3px; border-radius: 0 3px 3px 0; background: ${COLORS.red}; }
.bbz-sb-item.is-active .bbz-sb-icon { color: ${COLORS.red}; }
.bbz-sb-item.has-active { color: #fff; }
.bbz-sb-item.has-active .bbz-sb-icon { color: ${COLORS.red}; }
.bbz-sb-sub { position: relative; margin: 2px 0 6px; padding-left: 0; }
.bbz-sb-sub::before { content: ''; position: absolute; left: 20px; top: 4px; bottom: 4px; width: 1px; background: rgba(255,255,255,.14); }
.bbz-sb-subitem { position: relative; display: block; padding: 7px 12px 7px 42px; margin-bottom: 1px; border-radius: 7px; color: ${COLORS.textMuted}; font-family: 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 13px; text-align: left; text-decoration: none; transition: background .15s, color .15s; }
.bbz-sb-subitem:hover { background: ${COLORS.hover}; color: #fff; text-decoration: none; }
.bbz-sb-subitem.is-active { background: rgba(255,255,255,.07); color: #fff; font-weight: 600; }
.bbz-sb-subitem.is-active::before { content: ''; position: absolute; left: 18px; top: 50%; width: 5px; height: 5px; margin-top: -2.5px; border-radius: 50%; background: ${COLORS.red}; }
.bbz-sb-foot { padding: 10px 10px 14px; border-top: 1px solid ${COLORS.border}; flex-shrink: 0; }
.bbz-sb-foot .bbz-sb-item { color: ${COLORS.textMuted}; }
.bbz-sb-foot .bbz-sb-item:hover { background: rgba(173,36,41,.2); color: #ff9a9a; }
`;

/* ───────────────────────── Menu items ───────────────────────── */
const MenuLink = ({ item, active }) => (
  <NavLinkRRD
    to={fullPath(item, item.path)}
    className={`bbz-sb-item${active ? " is-active" : ""}`}
    aria-current={active ? "page" : undefined}
  >
    <span className="bbz-sb-icon">{iconFor(item.name)}</span>
    <span className="bbz-sb-label">{item.name}</span>
  </NavLinkRRD>
);

const MenuGroup = ({ item, activeLeaf, open, onToggle }) => {
  const containsActive = !!activeLeaf && activeLeaf.parentName === item.name;
  const groupId = `bbz-sb-group-${item.name.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div>
      <button
        type="button"
        className={`bbz-sb-item${containsActive ? " has-active" : ""}`}
        aria-expanded={open}
        aria-controls={groupId}
        onClick={onToggle}
      >
        <span className="bbz-sb-icon">{iconFor(item.name)}</span>
        <span className="bbz-sb-label">{item.name}</span>
        <Chevron open={open} />
      </button>
      {open && (
        <div className="bbz-sb-sub" id={groupId}>
          {item.subMenu.map((sub) => {
            const active = activeLeaf === sub || (activeLeaf && activeLeaf.path === sub.path && activeLeaf.parentName === item.name);
            return (
              <NavLinkRRD
                key={sub.path}
                to={fullPath(sub, sub.path)}
                className={`bbz-sb-subitem${active ? " is-active" : ""}`}
                aria-current={active ? "page" : undefined}
              >
                {sub.name}
              </NavLinkRRD>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ───────────────────────── Sidebar ───────────────────────── */
const Sidebar = (props) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const { routes, logo } = props;

  // Menu = routes with a name, in the /admin layout
  const menu = useMemo(() => {
    const seen = new Set();
    return (routes || []).filter((r) => {
      if (!r.name || r.layout !== "/admin" || seen.has(r.name)) return false;
      seen.add(r.name);
      return true;
    });
  }, [routes]);

  const leaves = useMemo(() => collectLeaves(menu), [menu]);
  const activeLeaf = findActiveLeaf(leaves, location.pathname);

  // Groups: the one holding the current page opens automatically; others toggle freely
  const [openGroups, setOpenGroups] = useState(() => new Set(activeLeaf?.parentName ? [activeLeaf.parentName] : []));
  useEffect(() => {
    if (activeLeaf?.parentName) {
      setOpenGroups((prev) => (prev.has(activeLeaf.parentName) ? prev : new Set([...prev, activeLeaf.parentName])));
    }
  }, [activeLeaf?.parentName]);

  const toggleGroup = (name) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const logOutHandler = () => {
    Swal.fire({
      title: "Sign out?",
      text: "Are you sure you want to sign out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.brand,
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Sign Out",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(LoginDetails({ token: null, name: null, userId: null, email: null, isLogin: false }));
        Navigate("/auth/login");
      }
    });
  };

  return (
    <aside className="bbz-sb" aria-label="Super admin navigation">
      <style dangerouslySetInnerHTML={{ __html: css }} />

      <div className="bbz-sb-brand">
        <Link to="/admin/index" className="bbz-sb-logo" style={{ textDecoration: "none" }}>
          {logo?.imgSrc ? (
            <img src={logo.imgSrc} alt={logo.imgAlt} style={{ height: 30, maxWidth: 160, objectFit: "contain", display: "block" }} />
          ) : (
            <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>BidBuy<span style={{ color: COLORS.brand }}>Zoom</span></span>
          )}
        </Link>
        <div className="bbz-sb-role">Super Admin</div>
      </div>

      <nav className="bbz-sb-nav">
        {menu.map((item) =>
          item.subMenu?.length ? (
            <MenuGroup
              key={item.name}
              item={item}
              activeLeaf={activeLeaf}
              open={openGroups.has(item.name)}
              onToggle={() => toggleGroup(item.name)}
            />
          ) : (
            <MenuLink key={item.name} item={item} active={activeLeaf === item} />
          )
        )}
      </nav>

      <div className="bbz-sb-foot">
        <button type="button" className="bbz-sb-item" onClick={logOutHandler}>
          <span className="bbz-sb-icon"><SignOutIcon /></span>
          <span className="bbz-sb-label">Sign Out</span>
        </button>
      </div>
    </aside>
  );
};

Sidebar.defaultProps = { routes: [{}] };
Sidebar.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink: PropTypes.string,
    imgSrc: PropTypes.string,
    imgAlt: PropTypes.string,
  }),
};

export default Sidebar;
